import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  useColorModeValue,
  SimpleGrid,
  Icon,
  CircularProgress,
  CircularProgressLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { Brain, Activity, Zap, Database, GitBranch } from 'lucide-react';

const PipelineHealthPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const pipelines = [
    {
      name: "Clinical Data Sync",
      type: "Bi-directional",
      status: "Active",
      health: 98,
      metrics: {
        throughput: "2.4K/s",
        latency: "45ms",
        errorRate: "0.02%"
      },
      source: "SentientIQ",
      destination: "Neural Nexus"
    },
    {
      name: "Knowledge Graph Updates",
      type: "Incremental",
      status: "Active",
      health: 100,
      metrics: {
        throughput: "850/s",
        latency: "120ms",
        errorRate: "0.00%"
      },
      source: "Neural Lab",
      destination: "Domain Graph"
    },
    {
      name: "Governance Validation",
      type: "Real-time",
      status: "Active",
      health: 95,
      metrics: {
        throughput: "1.2K/s",
        latency: "65ms",
        errorRate: "0.05%"
      },
      source: "Admin Portal",
      destination: "Clinical Portal"
    }
  ];

  const integrationHealth = [
    {
      component: "Admin-Clinical Bridge",
      status: "Healthy",
      metrics: {
        uptime: "99.99%",
        responseTime: "52ms",
        dataSync: "98.2%"
      }
    },
    {
      component: "Knowledge Base Sync",
      status: "Healthy",
      metrics: {
        uptime: "100%",
        responseTime: "88ms",
        dataSync: "100%"
      }
    },
    {
      component: "Governance Layer",
      status: "Warning",
      metrics: {
        uptime: "99.95%",
        responseTime: "142ms",
        dataSync: "94.8%"
      }
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* Integration Health */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Integration Health</Text>
        <SimpleGrid columns={3} spacing={4}>
          {integrationHealth.map((item, idx) => (
            <Box
              key={idx}
              bg={bgColor}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <VStack align="start" spacing={3}>
                <HStack justify="space-between" width="100%">
                  <Text fontWeight="medium">{item.component}</Text>
                  <Badge 
                    colorScheme={item.status === 'Healthy' ? 'green' : 'orange'}
                  >
                    {item.status}
                  </Badge>
                </HStack>
                <SimpleGrid columns={2} spacing={2} width="100%">
                  <Text fontSize="sm">Uptime:</Text>
                  <Text fontSize="sm" fontWeight="medium">{item.metrics.uptime}</Text>
                  <Text fontSize="sm">Response:</Text>
                  <Text fontSize="sm" fontWeight="medium">{item.metrics.responseTime}</Text>
                  <Text fontSize="sm">Sync Rate:</Text>
                  <Text fontSize="sm" fontWeight="medium">{item.metrics.dataSync}</Text>
                </SimpleGrid>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Data Pipelines */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Active Pipelines</Text>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Pipeline</Th>
              <Th>Health</Th>
              <Th>Metrics</Th>
              <Th>Flow</Th>
            </Tr>
          </Thead>
          <Tbody>
            {pipelines.map((pipeline, idx) => (
              <Tr key={idx}>
                <Td>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Icon as={Database} color="methodist.blue" />
                      <Text fontWeight="medium">{pipeline.name}</Text>
                    </HStack>
                    <Badge colorScheme="purple">{pipeline.type}</Badge>
                  </VStack>
                </Td>
                <Td>
                  <CircularProgress 
                    value={pipeline.health} 
                    color={pipeline.health > 95 ? "green.400" : "orange.400"}
                    size="50px"
                  >
                    <CircularProgressLabel>{pipeline.health}%</CircularProgressLabel>
                  </CircularProgress>
                </Td>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm">Throughput: {pipeline.metrics.throughput}</Text>
                    <Text fontSize="sm">Latency: {pipeline.metrics.latency}</Text>
                    <Text fontSize="sm">Error Rate: {pipeline.metrics.errorRate}</Text>
                  </VStack>
                </Td>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" color="gray.500">From: {pipeline.source}</Text>
                    <Icon as={Activity} size={12} />
                    <Text fontSize="sm" color="gray.500">To: {pipeline.destination}</Text>
                  </VStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};

export default PipelineHealthPanel;
